import { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../../common/interactive/CustomSelect";
import RangeDatePickerFilter from "../../pages/custom/filters/RangeDatePickerFilter";
import { getLast6Months, getLastMonth, getLastWeek, getThisMonth, getThisYear, getThisWeek } from "../../../../lib/dateScripts";
import { formatIncidentDateFilterLabel, normalizeIncidentDateFilterValue } from "../../../../lib/filters";

const OPERATOR_OPTIONS = [
    { value: 'ge', label: 'On or after (GE)' },
    { value: 'le', label: 'On or before (LE)' },
    { value: 'range', label: 'Date range' },
]

function deriveStateFromStored(stored) {
    if (!stored) {
        return { op: 'range', pickerValue: null }
    }

    if (typeof stored === 'string') {
        return { op: 'range', pickerValue: stored }
    }

    if (typeof stored === 'object' && !Array.isArray(stored)) {
        const op = stored.op === 'ge' || stored.op === 'le' || stored.op === 'range' ? stored.op : 'range'
        if (op === 'ge' || op === 'le') {
            return { op, pickerValue: stored.date || null }
        }
        if (stored.start && stored.end) {
            return {
                op: 'range',
                pickerValue: stored.start === stored.end ? stored.start : `${stored.start}|${stored.end}`,
            }
        }
        return { op: 'range', pickerValue: null }
    }

    return { op: 'range', pickerValue: null }
}

export default function DateOperatorOption({ filtersValues, item, mainSelectAction, classes, workQ }) {
    const stored = filtersValues?.[item.key]
    const derived = useMemo(() => deriveStateFromStored(stored), [stored])

    const [op, setOp] = useState(derived.op)
    const [hideItem, setHideItem] = useState(false)
    const [minMaxDates, setMinMaxDates] = useState(null)

    useEffect(() => {
        setOp(derived.op)
    }, [derived.op])

    useEffect(() => {
        if (workQ && workQ.filter_conditions) {
            if (workQ.filter_conditions[`${item.key}_filter_type`] && workQ.filter_conditions[`${item.key}_filter_type`] == 1) {
                switch (workQ.filter_conditions[item.key]?.type) {
                    case 'ge':
                    case 'le':
                    case 'today':
                    case 'tomorrow':
                        setHideItem(true)
                        break
                    case 'this_week':
                        setMinMaxDates(getThisWeek(new Date(), true))
                        break
                    case 'last_week':
                        setMinMaxDates(getLastWeek(new Date(), true))
                        break
                    case 'this_month':
                        setMinMaxDates(getThisMonth(new Date(), true))
                        break
                    case 'last_month':
                        setMinMaxDates(getLastMonth(true))
                        break
                    case 'last_6_month':
                        setMinMaxDates(getLast6Months(true))
                        break
                    case 'this_year':
                        setMinMaxDates(getThisYear(new Date(), true))
                        break
                    default:
                        break
                }
            }
        }
    }, [workQ, item])

    function commit(nextValue) {
        const normalized = normalizeIncidentDateFilterValue(nextValue)
        mainSelectAction({
            data: {
                value: normalized,
            },
            identifier: { type: item.key },
            option: {
                label: formatIncidentDateFilterLabel(normalized) || normalized,
                value: normalized,
            },
        })
    }

    function onOperatorChange(payload) {
        const nextOp = payload?.data?.value || payload?.option?.value
        if (!nextOp) {
            return
        }

        setOp(nextOp)

        // Operator change clears the previous date selection so GE/LE vs range stay consistent.
        commit(null)
    }

    function onDateChange(payload) {
        const raw = payload?.data?.value

        if (!raw) {
            commit(null)
            return
        }

        if (op === 'ge' || op === 'le') {
            const date = typeof raw === 'string' && raw.includes('|') ? raw.split('|')[0] : raw
            commit({ op, date })
            return
        }

        if (typeof raw === 'string' && raw.includes('|')) {
            const [start, end] = raw.split('|')
            commit({ op: 'range', start, end })
            return
        }

        commit({ op: 'range', start: raw, end: raw })
    }

    if (hideItem) {
        return null
    }

    const dateLabel = op === 'ge'
        ? 'On or after date'
        : op === 'le'
            ? 'On or before date'
            : item.label

    return (
        <div className={classes.row}>
            <div className={classes.date_operator_block}>
                <div className={classes.date_operator_label}>{item.label}</div>
                <div className={classes.date_operator_row}>
                    <CustomSelect
                        preSelectedValue={op}
                        optionsData={OPERATOR_OPTIONS}
                        label="Compare"
                        identifier={{ type: `${item.key}_op` }}
                        action={onOperatorChange}
                        isAFilter={true}
                    />
                    <RangeDatePickerFilter
                        preSelectedValue={derived.pickerValue}
                        label={dateLabel}
                        identifier={{ type: item.key }}
                        fullWidth={true}
                        action={onDateChange}
                        minMax={minMaxDates}
                    />
                </div>
            </div>
        </div>
    )
}
